<template>
  <div @click="selectedInputItem = null">
    <breadcrumbs home="Blend">
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <production-blending-preferences @update="preferences=$event"/>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container fluid class="pt-6 px-6 purtrak-scrollable">

          <v-overlay absolute :opacity="0.15" style="height:calc(100vh - 158px)" v-if="showWarning">
            <div style="position:relative;top:-100px;width:400px" class="d-inline-block purple white--text text-body-1 pa-5">
              Before you blend, you must have
              <template v-if="spirits.length===0">
                spirits defined.
                <div class="mt-10 float-right">
                  <v-btn :to="{name:'Spirits'}" color="primary" small>Spirits</v-btn>
                </div>
              </template>
              <template v-else-if="allTanks.length===0">
                tanks defined.
                <div class="mt-10 float-right">
                  <v-btn :to="{name:'EquipmentTanks'}" color="primary" small>Tanks</v-btn>
                </div>
              </template>
            </div>
          </v-overlay>

          <v-row dense>
            <v-col cols="6" sm="4" md="3">
              <date-picker v-model="blend.date" @change="refresh();forDateAndSite();refreshLot()"/>
            </v-col>
            <v-col cols="6" sm="3" md="2" v-if="preferences.blendDuration">
              <duration-picker label="Duration" v-model="blend.duration" dense/>
            </v-col>
            <v-col cols="4" sm="2" v-if="preferences.blendIncomplete">
              <v-checkbox label="Completed" v-model="blend.completed" dense class="mt-1" @change="validate(false)"/>
            </v-col>
            <v-col cols="4" sm="3" lg="2" :offset-md="3+(preferences.blendDuration?0:2)+(preferences.blendIncomplete?0:2)">
              <v-text-field label="#" v-model="blend.number" :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined dense :counter="20"/>
            </v-col>
          </v-row>
          <v-row dense v-if="sites.length>1" >
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete v-model="blend.siteId" outlined dense label="Site" :items="sites" item-text="name" item-value="id" @change="forDateAndSite"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-menu right max-width="300" nudge-bottom="12" :open-on-click="false" :close-on-click="false" :close-on-content-click="false" content-class="tour-arrow tour-arrow-left purple white--text pt-6 px-4" transition="slide-y-transition" :value="noFormulas">
                <template v-slot:activator="{on}">
                  <v-autocomplete v-model="blend.formulaId" outlined dense label="Formula" :items="formulas" item-text="name" item-value="id"
                                  @change="selectFormula();refreshLot()" v-on="on">
                    <template v-slot:item="{item}">
                      <v-list-item-content>{{item.name}}</v-list-item-content>
                      <v-list-item-action class="text-caption">
                        <template v-if="item.excise==='SpiritsReductionBulkSpiritsReturnedToSpiritProduction'">Spirit</template>
                        <template v-else-if="item.excise==='WineReductionNonDutyPaid'">Wine</template>
                        <template v-else-if="item.excise==='UserSpiritsReductionFormulationOther'">Approved formulation</template>
                        <template v-else-if="item.id!==''">Denature</template>
                      </v-list-item-action>
                    </template>
                  </v-autocomplete>
                </template>
                You can create formulas for consistency and to save time.
                <v-btn plain class="text-none mt-4 white--text" :to="{name:'ProductionBlendingFormulas'}">Go to formulas</v-btn>
                <div class="mt-1">
                  <v-btn plain class="white--text mt-4 float-right" @click.stop="noFormulas=false">Okay</v-btn>
                </div>
              </v-menu>
            </v-col>
          </v-row>

          <v-row v-if="formula && formula.description" dense class="mt-n4 mb-4">
            <v-col cols="12" lg="8" class="primary--text">
              <em>{{formula.description}}</em>
            </v-col>
          </v-row>

          <v-row dense align="center">
            <v-col cols="12" sm="6" md="4">
              <v-select :items="[{text:'Spirit',value:'SpiritsReductionBulkSpiritsReturnedToSpiritProduction'},
                                 {text:'Wine',value:'WineReductionNonDutyPaid'},
                                 {text:'Approved formulation',value:'UserSpiritsReductionFormulationOther'},{divider:true},
                                 {text:'Denatured alcohol - DA-2F',value:'SpiritsReductionDenaturedSpiritsFuelDA2F'},
                                 {text:'Denatured alcohol - DA-2G',value:'SpiritsReductionDenaturedSpiritsFuelDA2G'},
                                 {text:'Denatured alcohol - Other grades',value:'SpiritsReductionDenaturedSpiritsOther'},
                                 {text:'Specially denatured alcohol',value:'SpiritsReductionSpeciallyDenaturedSpirits'},
/*
                                 {divider:true},
                                 {text:'Other non-excise',value:'None'}
                                 */]"
                        @change="blend.outputId='';refreshLot();validate(true)" :disabled="!!blend.formulaId"
                        label="Produces" v-model="blend.excise" outlined dense/>
            </v-col>
            <v-col cols="6" sm="3" md="2" v-if="blend.excise==='UserSpiritsReductionFormulationOther'">
              <v-text-field label="Formula number" v-model="blend.approvedFormulation" :rules="[requiredApprovedFormulation,fieldRules.maxLength(10)]" :counter="10" outlined dense  :disabled="!!blend.formulaId"/>
            </v-col>
            <v-col cols="12" sm="6" class="text-caption mt-n6" v-else-if="!['SpiritsReductionBulkSpiritsReturnedToSpiritProduction','WineReductionNonDutyPaid'].includes(blend.excise)">
              <a href="https://laws-lois.justice.gc.ca/eng/regulations/sor-2005-22/FullText.html" target="_blank">Denatured and Specially Denatured Alcohol Regulations <v-icon x-small>mdi-open-in-new</v-icon></a>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete :label="`Blend to produce ${blend.excise==='SpiritsReductionBulkSpiritsReturnedToSpiritProduction'?'spirit':(blend.excise==='WineReductionNonDutyPaid'?'wine':'non-excise')}`"
                              dense outlined v-model="blend.outputId" :disabled="!adhoc" :rules="[requiredAlways]"
                              no-data-text="None found"
                              :items="outputsProducedList" item-text="name" item-value="id"/>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <tank-select v-model="blend.tankId" :tanks="tanks" label="Destination tank" outlined :required="blend.completed" :hint="blendId===undefined"
                          :highlight="tank => blendId === undefined && tank.contents.alcoholId === blend.outputId"/>
            </v-col>
            <v-col class="mt-2" v-if="!$vuetify.breakpoint.mobile && blend.excise==='SpiritsReductionBulkSpiritsReturnedToSpiritProduction'"><v-btn small plain class="text-none" :disabled="!edit" @click="calculateFromTargetLAA">Producing {{laa | number(3)}} LAA</v-btn></v-col>
          </v-row>

          <v-row dense>
            <v-col cols="4" sm="3" lg="2" v-if="preferences.blendLot">
              <v-text-field v-model="blend.lot" label="Produced lot" outlined dense :rules="[required,fieldRules.maxLength(20)]" :counter="20"/>
            </v-col>

            <v-col cols="4" sm="3" lg="2" v-if="blend.excise !== 'WineReductionNonDutyPaid'">
              <v-switch label="Blend to volume" class="mt-1" v-model="blend.blendToVolume" :disabled="!!blend.formulaId"
                        @change="laa = blend.blendToVolume ? blend.producedVolume * blend.blendStrength / 100 : changeLAA(true)"/>
            </v-col>

            <v-col cols="4" sm="3" lg="2" v-if="blend.excise !== 'SpiritsReductionBulkSpiritsReturnedToSpiritProduction' || trackLitres || blend.blendToVolume">
              <decimal-number-input label="Produced volume" v-model="blend.producedVolume"
                      prefix="" suffix="litres" :precision="0"
                      :disabled="blend.excise === 'SpiritsReductionBulkSpiritsReturnedToSpiritProduction' && trackLitres && !blend.blendToVolume"
                      @change="changeProducedVolume"/>
            </v-col>

            <v-col cols="4" sm="3" lg="2" v-if="blend.excise === 'WineReductionNonDutyPaid' || blend.excise === 'SpiritsReductionBulkSpiritsReturnedToSpiritProduction' && blend.blendToVolume">
              <alcohol-input label="Strength" strength-only :strength.sync="blend.blendStrength"
                            :disabled="formula.blendToVolume"
                            @change="laa = blend.excise === 'WineReductionNonDutyPaid' ? laa : blend.producedVolume * blend.blendStrength / 100"/>
            </v-col>

          </v-row>

          <v-row>
            <v-col cols="12">
              <outlined-block :error-messages="inputsError" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="blend.inputs" item-key="key" no-data-text="At least one input item is required"
                              :headers="headers"  class="no-sort"
                              :item-class="item => item.key=== selectedInputItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedInputItem = item.key; $nextTick(() => this.$refs.form.validate())}"
                              ref="inputsTableBody">
                  <template v-slot:item.add="{item,index}">
                    <v-icon :disabled="!edit" class="handle mt-n2">mdi-drag</v-icon>
                    <v-btn :disabled="item.static || !edit" class="mt-n2" icon @click.stop="deleteInput(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.inputType="{item}">
                    <multi-level-select v-if="selectedInputItem === item.key" :menuData="inputTypes" :first.sync="item.type" :second.sync="item.id"
                                  :hint="blend.blendToVolume?'':item.formulaPercent" persistent-hint outlined label="Input"
                                  :disabled="item.static" @change="inputChangeType(item, item.id)"/>
                    <div v-else class="ml-1" :class="{'error--text':!item.type}">{{itemDescription(item)}}</div>
                  </template>
                  <template v-slot:item.inputSourceType="{item}">
                    <template v-if="selectedInputItem === item.key">
                      <vessel-select v-if="(item.type === 'Spirit' || item.type === 'Wine' || item.type === 'NonExcise')"
                                  :tanks="tanks" :bulkContainers="bulkContainers" :vessel.sync="item.vesselType" :id.sync="item.vesselId"
                                  :hint="blendId===undefined" label="Vessel"
                                  :highlight="vessel => blendId === undefined && vessel.contents.alcoholId === item.id && (blend.excise==='UserSpiritsReductionFormulationOther' && vessel.contents.userLicense || blend.excise!=='UserSpiritsReductionFormulationOther' && !vessel.contents.userLicense)"/>
                      <v-combobox v-else-if="item.type==='Item' && lotTracking(item)"
                                  :items="currentLots(item)" prefix="" v-model="item.lot" dense
                                  :rules="[required,fieldRules.maxLength(20)]"
                                  class="text-caption"
                                  :hint="blendId===undefined?inputItem(item).quantity:''" persistent-hint
                                  @change="changeItemLot(item)" outlined label="Lot"/>
                      <div v-else-if="item.type === 'Item' && inputItem(item).type === 'Item' && blendId===undefined" class="mt-n5 ml-1 text-caption">
                        {{inputItem(item).quantity}}
                      </div>
                      <v-btn v-if="['Spirit','Wine','NonExcise'].includes(item.type) && item.vesselId" x-small text @click="allContents(item)">
                        Use all contents
                      </v-btn>
                    </template>
                    <template v-else>
                      <div v-if="(item.type === 'Spirit' || item.type === 'Wine' || item.type === 'NonExcise')" class="ml-1" :class="{'error--text':!item.vesselId && blend.completed}">
                        {{vesselName(item)}}
                        <div v-if="blendId===undefined" class="text-caption">{{vesselVolume(item)}}</div>
                      </div>
                      <div v-else-if="item.type === 'Item' && lotTracking(item)" class="ml-1" :class="{'error--text':required(item.lot)!==true}">
                        Lot: {{required(item.lot) === true ? item.lot : 'MISSING'}}
                        <div v-if="blendId===undefined" class="text-caption">{{inputItem(item).quantity}}</div>
                      </div>
                      <div v-else-if="item.type === 'Item' && inputItem(item).type === 'Item' && blendId===undefined" class="ml-1 text-caption">
                        {{inputItem(item).quantity}}
                      </div>
                    </template>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="item.type !== 'Spirit' || blend.blendToVolume">
                      <template v-if="selectedInputItem === item.key">
                        <decimal-number-input label="Quantity" v-model="item.quantity" outlined
                                prefix=""
                                :suffix="item.type === 'Item' && item.id && items.length ? inputItem(item).trackUnits : 'litres'"
                                :precision="item.type === 'Item' ? inputItem(item).precision : 3"
                                @change="blend.excise === 'WineReductionNonDutyPaid' ? changeLAA(true) : blendToVolumeCalc()"/>
                        <decimal-number-input v-if="item.type != 'Item' && company.settings.trackKg" label="kg" v-model="item.kg" outlined
                                prefix="" suffix="kg" :precision="3"/>
                      </template>
                      <div v-else class="mr-1" :class="{'error--text':item.quantity<0}">
                        {{item.quantity | number(item.type === 'Item' ? inputItem(item).precision : 3)}}
                        {{item.type === 'Item' && item.id && items.length ? inputItem(item).trackUnits : 'litres'}}
                        <div v-if="item.type !== 'Item' && blend.blendToVolume" class="text-caption">{{item.quantity*item.strength/100 | number(3)}} LAA</div>
                      </div>
                    </template>
                    <div v-else-if="company.settings.trackLitres" class="mr-1">{{item.quantity | number(3)}} litres</div>
                  </template>
                  <template v-slot:item.laa="{item}">
                    <template v-if="item.type === 'Spirit' || item.type === 'Wine' || item.type === 'NonExcise'">
                      <template v-if="blend.blendToVolume">
                        <alcohol-input v-if="selectedInputItem === item.key" label="Strength" strength-only :strength.sync="item.strength" @change="blendToVolumeCalc()"/>
                        <div v-else class="mr-1" :class="{'error--text':item.strength<0 || item.strength > 99}">{{item.strength | number(2)}}% alc./vol.</div>
                      </template>
                      <template v-else>
                        <alcohol-input v-if="selectedInputItem === item.key" :disabled="!edit" label="LAA" outlined
                                        spirit v-model="item.laa" :litres.sync="item.quantity" :strength.sync="item.strength" :kg.sync="item.kg"
                                        :hint="item.percent" persistent-hint @change="changeLAA(true)"
                                        :tare="tare(item)"/>
                        <div v-else class="mr-1">
                          {{item.laa | number(3)}} LAA
                          <div class="text-caption">{{item.percent}}<template v-if="formula.percentTolerance"> ± {{ formula.percentTolerance }}%</template></div>
                        </div>
                      </template>
                    </template>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedInputItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn :disabled="!edit" fab color="primary" x-small @click.stop="addInput"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea label="Comments" rows="3"
                           v-model="blend.comments" outlined dense :rules="[fieldRules.maxLength(500)]" counter="500"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="blendId && company.permissions.includes('production.write')">
          <v-switch v-if="blend.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0" @change="selectFormula(false)"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="calculate" max-width="300">
      <v-card>
        <v-card-title>Calculate volumes</v-card-title>
        <v-divider/>
        <v-container class="mt-4">
          <v-row>
            <v-col cols="7"><decimal-number-input label="Target output" :precision="1" suffix="LAA" prefix="" v-model="targetLAA"/></v-col>
          </v-row>
        </v-container>

        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer></v-spacer>
            <v-btn plain color="primary" @click="calculate=false">Cancel</v-btn>
            <v-btn color="primary" @click="doCalculate">Calculate</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-leave v-model="next"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns';
import sortableJS from 'sortablejs';
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'
import BulkContainerSelect from '@/components/BulkContainerSelect'
import ProductionBlendingPreferences from '@/views/production/blending/ProductionBlendingPreferences'
import createHierarchyList from '@/mixins/createHierarchyList'
import VesselSelect from '@/components/VesselSelect'

export default {
  name: 'ProductionBlendingEdit',
  components: {TankSelect,BulkContainerSelect,ProductionBlendingPreferences,VesselSelect},
  mixins: [confirmLeave,createHierarchyList],
  props: {
    blendId: {type:String, required:false},
    formulaId: {type:String, required:false},
    volume: {type:Number, required:false, default:0},
    activityId: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      laa: 0,
      calculate: false,
      targetLAA: 0,
      blend: {date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),number:'',siteId:this.$store.getters.sites(true, {B266:true})[0].id,
              outputId:'',tankId:'',formulaId:'',blendToVolume:false, blendStrength:7,
              excise:'SpiritsReductionBulkSpiritsReturnedToSpiritProduction',approvedFormulation:'',
              comments:'',inputs:[], producedVolume:0, lot:'',completed:true,duration:0,
              activityId:this.activityId},
      formulas: [{id:'',name:'AD HOC (no formula)'},{divider:true}],
      preferences: {},
      noFormulas: false,
      stills: [],
      spirits: [],
      nonExcises: [],
      wines: [],
      allTanks: [],
      allBulkContainers: [],
      items: [],
      categories: [],

      outputOpen: false,
      inputsError: null,

      dirty: false,
      saving: false,
      loading: false,
      valid: true,
      selectedInputItem: null,
      requiredAlways: v => !!v || 'Required',
      requiredApprovedFormulation: v => this.blend.excise !== 'UserSpiritsReductionFormulationOther' ||  !!v || 'Required',
      required: v => !this.blend.completed || !!v || 'Required',
      sortable: null,
      showWarning: false
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    adhoc() {
      return this.blend.formulaId == '';
    },
    sites() {
      return this.$store.getters.sites(!this.blendId, {B266:true});
    },
    tanks() {
      return this.allTanks.filter(tank => tank.site.id === this.blend.siteId);
    },
    bulkContainers() {
      return this.allBulkContainers.filter(bulkContainer => bulkContainer.contents.siteId === this.blend.siteId);
    },
    formula() {
      return this.formulas.find(formula => formula.id === this.blend.formulaId);
    },
    outputsProducedList() {
      const list = {SpiritsReductionBulkSpiritsReturnedToSpiritProduction:this.spirits, WineReductionNonDutyPaid:this.wines}[this.blend.excise] ?? this.nonExcises;
//      const list = this.blend.excise === 'SpiritsReductionBulkSpiritsReturnedToSpiritProduction' ? this.spirits : this.nonExcises;
      if (this.blendId || !this.adhoc)
        return list;
      return list.filter(spirit => spirit.active);
    },
    inputTypes() {
      const types = [];
      if (this.spirits.length && this.blend.excise !== 'WineReductionNonDutyPaid')
        types.push({name:'Spirit',id:'Spirit',menu:this.spirits});
      if (this.wines.length)
        types.push({name:'Wine',id:'Wine',menu:this.wines});
      if (this.nonExcises.length)
        types.push({name:'Non-excise',id:'NonExcise',menu:this.nonExcises});
      if (this.items.length) {
        if (this.categories.length) {
          const categories = this.createHierarchyList(this.categories);
          for (const category of categories) {
            const sub = this.items.filter(item => item.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
//          types.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
          types.push({name:'Item',id:'Item',menu:categories});
          const leftOver = this.items.filter(item => !item.category?.id);
          if (leftOver.length)
            types[types.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
        }
        else
          types.push({name:'Item',id:'Item',menu:this.items});
      }
      return types;
    },
    headers() {
      const headers = [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                      {text:'Input',value:'inputType',width:'30%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top'},
                      {text:'Source',value:'inputSourceType',width:'30%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top'},
                      {text:'Quantity',value:'quantity',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top',align:'right'}]
      if (this.blend.excise !== 'WineReductionNonDutyPaid')
        headers.push({text:this.blend.blendToVolume?'Strength':'LAA',value:'laa',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top',align:'right'});
      return headers;
    }
  },
  watch: {
    'blend.inputs': {
      handler() {
        if ((this.blend.excise === 'WineReductionNonDutyPaid' && !this.blendId) ||
            (this.blend.excise === 'SpiritsReductionBulkSpiritsReturnedToSpiritProduction' && this.trackLitres && !this.blend.blendToVolume))
          this.blend.producedVolume = this.blend.inputs.reduce((total, input) => total + (input.type !== 'Item' ? input.quantity : 0), 0);
        this.dirty = true;
      },
      immediate: true,
      deep: true
    }
  },
  async mounted() {
    this.loading = true;
    await Promise.all([
      this.$lazyListLoad(this.formulas, 'formulas2', 'id,name,approvedFormulation,percentTolerance,outputName,scaleToId,scaleBase,description,excise,blendToVolume,blendStrength', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.categories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(this.items, 'items2', 'id,name,sku,trackUnits,precision,type,lotTracking,category{id}', 100, !this.blendId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name,active', 100),
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name,active', 100),
      this.company.allowWine ? this.$lazyListLoad(this.wines, 'wines2', 'id,name,active', 100, this.blendId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : ''
    ]);
    const results = await this.$apiCall(`query ProductionBlendingEdit($edit:Boolean!, $id:ID!) {
                                              blend(id:$id) @include(if:$edit) {
                                                id,formulaId,date,number,siteId,comments,outputId,excise,tankId,volume,lot,blendToVolume, blendStrength
                                                completed,duration,created,approvedFormulation,litres
                                                inputs {type,id,quantity,kg,strength,laa,vesselType,vesselId,lot},
                                              }
                                              blendingPreferences {
                                                blendDuration,blendIncomplete,blendLot
                                              }
                                            }`,
                                        {edit:this.blendId!=undefined,id:this.blendId || ''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.preferences = results.data.blendingPreferences;

    if (this.blendId) {
      await this.$lazyListLoad(this.allTanks, 'tanks2', 'id,name,site{id}', 100);
      this.$lazyListLoad(this.allBulkContainers, 'bulkContainers2', 'id,name,contents{siteId}', 100);
      let key = Date.now();
      results.data.blend.inputs.forEach(input => input.key = key++);
      this.blend = results.data.blend;
      this.edit = !this.blend.completed;
      if (!this.blend.completed)
        this.blend.duration = this.$roundNumber(Date.now()/60000 - this.blend.created, 0);
      this.$set(this.blend, 'producedVolume', this.blend.blendToVolume ? this.blend.litres : this.blend.volume);
      if (this.blend.blendToVolume)
        this.laa = this.blend.producedVolume * this.blend.blendStrength / 100;
      delete this.blend.volume;
      delete this.blend.litres;
      if (!this.blend.formulaId) {
        if (this.formulas.length)    // In case we de-select adhoc
          this.blend.formulaId = this.formulas[0].id;
      }
      else if (!this.blend.completed)
        this.selectFormula(false);
    }
    else {
      const results = await this.$apiCall(`query ProductionBlendingEdit {
                                              blendNextNumber
                                            }`);
      if (!results.error)
        this.blend.number = results.data.blendNextNumber;
      await this.refresh();
      this.blend.formulaId = this.formulaId || '';
      if (this.formula.id && !this.formula.blendToVolume)
        this.targetLAA = this.volume;
      await this.selectFormula();
      if (this.formula.id && this.formula.blendToVolume) {
        this.blend.producedVolume = this.volume;
        this.changeProducedVolume();
      }
//      if (this.formula.id && !this.formula.blendToVolume)
//        this.doCalculate();
      this.edit = true;
      this.noFormulas = this.formulas.length === 2 && !this.$vuetify.breakpoint.mobile && this.spirits.length && this.allTanks.length;
    }

    await this.refreshLot();
    this.$nextTick(() => {
      this.dirty = false;
      this.$refs.form.validate();
    });

    await this.changeLAA(true);
    if (this.blendId && this.blend.formulaId)
      this.blend.inputs.forEach(input => {input.formulaPercent = input.percent; input.static = true;});

    this.loading = false;

    setTimeout(() => this.showWarning = this.spirits.length===0 || this.allTanks.length===0, 2_000);

    const _self = this;
    this.sortable = sortableJS.create(this.$refs.inputsTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.blend.inputs.splice(oldIndex, 1)[0];
        _self.blend.inputs.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortable.destroy();
  },

  methods: {
    allContents(item) {
      const vesselsList = {'Tank':this.tanks,'BulkContainer':this.bulkContainers}[item.vesselType];
      const vessel = vesselsList.find(v => v.id === item.vesselId);
      if (vessel) {
        item.laa = vessel.contents.volume;
        item.litres = vessel.contents.litres;
        item.kg = vessel.contents.kg;
        item.strength = vessel.contents.strength;
        this.blendToVolumeCalc();
        this.changeLAA(true);
      }
    },
    changeProducedVolume() {
      this.laa = this.blend.blendToVolume ? this.blend.producedVolume * this.blend.blendStrength / 100 : this.laa;
      if (this.blend.blendToVolume)
        this.blend.inputs.forEach(input => {
          const formulaInput = this.formula?.inputs?.find(fi => fi.id === input.id);
          if (formulaInput) {
            input.quantity = this.blend.producedVolume * formulaInput.quantity;
            input.strength = formulaInput.strength;
          }
        });
    },
    calculateFromTargetLAA() {
      if (!this.adhoc && !this.blend.blendToVolume) {
        this.targetLAA = this.laa;
        this.calculate = true;
      }
    },
    blendToVolumeCalc() {
      if (!this.blend.blendToVolume)
        return;
      this.laa = 100;
      this.blend.producedVolume = 0;
      this.blend.inputs.forEach(input => {
        if (input.type !== 'Item')
          this.blend.producedVolume += input.quantity;
        else {
          const trackUnits = this.inputItem(input).trackUnits.toLowerCase();
          if (trackUnits === 'l' || trackUnits === 'litres')
            this.blend.producedVolume += input.quantity;
        }
      });
      this.laa =  this.blend.producedVolume * this.blend.blendStrength / 100;
    },
    itemDescription(item) {
      if (!item.type)
        return 'MISSING';
      const lists = {'Spirit':this.spirits,'Wine':this.wines,'NonExcise':this.nonExcises,'Item':this.items};
      return `${item.type}: ${lists[item.type]?.find(i => i.id === item.id || i.id === item.spiritId)?.name}`;
    },
    vesselName(item) {
      const name = {'Tank':this.tanks,'BulkContainer':this.bulkContainers}[item.vesselType]?.find(v => v.id === item.vesselId)?.name;
      return name ? `${item.vesselType==='Tank'?'Tank: ':''}${name}` : 'MISSING';
    },
    vesselVolume(item) {
      const vessel = {'Tank':this.tanks,'BulkContainer':this.bulkContainers}[item.vesselType]?.find(v => v.id === item.vesselId);
      if (vessel?.contents) {
        let amount = '(empty)';
        if (vessel.contents.alcoholType && vessel.contents.volume) {
          const spirits = vessel.contents.alcoholType === 'Spirit';
          amount = `${this.$number(vessel.contents.volume, 3)} ${spirits?'LAA':'litres'} ${vessel.contents.alcoholName}`;
        }
        return amount;
      }
    },
    validate(scaleInputs) {
      this.inputsError = null;
      setTimeout(() => this.$refs.form.validate());
      this.changeLAA(scaleInputs);
    },
    refresh() {
      if (this.blendId)
        return;
      this.allTanks = [];
      this.allBulkContainers = [];
      this.$lazyListLoad(this.allTanks, 'tanks2', `id,name,site{id},contents{volume,strength,kg,alcoholType,alcoholName,alcoholId,userLicense}`, 100, [{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.blend.date}]);
      this.$lazyListLoad(this.allBulkContainers, 'bulkContainers2', `id,name,contents{siteId,volume,strength,kg,alcoholType,alcoholName,alcoholId,userLicense}`, 100, [{field:'contents.volume', comparator:'ne', value:'0'}], [{name:'asof', value:this.blend.date}]);
    },
/*
    vesselContents(type, id) {
      if (this.blendId)
        return;
      const vessel = (type === 'Tank' ? this.tanks: this.bulkContainers).find(vessel => vessel.id === id);
      const spirit = !vessel || vessel.contents.alcoholType === 'Spirit';
      return vessel ? `${this.$number(vessel.contents.volume,3)} ${spirit?'LAA':'litres'}${vessel.contents.volume?` of ${vessel.contents.alcoholName}`:''}` : '';
    },
    */
    tare(input) {
      if (input.vesselType !== 'Tank' || !this.tanks.length || !input.vesselId)
        return 0;
      const tank = this.tanks.find(t => t.id === input.vesselId);
      return tank ? tank.tare : 0;
    },
    currentLots(item) {
//      const itemObject = this.items.find(i => i.id === item.id);
      const itemObject = this.inputItem(item);
      return itemObject.sites ? itemObject.sites.filter(site => site.siteId === this.blend.siteId).map(site => site.lot) : [];
    },
    changeItemLot(item) {
//      const itemObject = this.items.find(i => i.id === item.id);
      const itemObject = this.inputItem(item);
      const site = itemObject.sites && itemObject.sites.find(site => site.siteId === this.blend.siteId && site.lot === item.lot);
      itemObject.quantity = `${site ? this.$number(site.quantity, itemObject.precision) : '0'} ${itemObject.trackUnits}`;
    },
    changeLAA(scaleItems) {
      this.inputsError = null;
      if (this.blend.blendToVolume)
        return;

      this.laa = 0;

      // Get map of unique alcohol inputs (e.g. could have multiple inputs for same spirit) with value =  LAA
      const types = new Map();
      this.blend.inputs.forEach(input => {
        if (input.type !== 'Item' && input.laa) {
          this.laa += input.laa;
          const laa = types.get(input.id);  // Does it exist already?
          types.set(input.id, (laa ? laa : 0) + input.laa);
        }
      });

      if (this.blend.completed && (this.blend.excise === 'SpiritsReductionBulkSpiritsReturnedToSpiritProduction' && this.laa === 0 ||
                                   this.blend.excise === 'WineReductionNonDutyPaid' && this.blend.producedVolume === 0))
        this.inputsError = 'Alcohol inputs must be specified';

      // If any type is base for item scaling, scale items appropriately
      // this.formula?.inputs won't be set on edit, which is okay since we shouldn't change anything on edit (yet)
      if (scaleItems && this.formula?.scaleToId && this.formula?.inputs) {
        let scaleFactor = 1;
        const base = types.get(this.formula.scaleToId);
        scaleFactor = base ? base / this.formula.scaleBase : 0;

        this.blend.inputs.forEach(input => {
          if (input.type === 'Item') {
            const formulaInput = this.formula.inputs.find(i => i.id === input.id);
              if (formulaInput)
                input.quantity = this.$roundNumber(formulaInput.quantity * scaleFactor, formulaInput.precision);
          }
        });
      }

      // Now calculate percent usage, replacing LAA with percent
      types.forEach((laa,type) => types.set(type, this.$number(this.$roundNumber(laa / this.laa * 100, 1), 1)+'%'));

      // Set LAA for each alcohol
      this.blend.inputs.forEach(input => {
        input.percent = types.get(input.id) || '0.0%';
//        if (!this.inputsError && input.formulaPercent && input.percent != input.formulaPercent && this.blend.completed) {
        if (!this.inputsError && input.formulaPercent && this.blend.completed &&
            (parseFloat(input.percent) > parseFloat(input.formulaPercent) + (this.formula?.percentTolerance ?? 0) ||
             parseFloat(input.percent) < parseFloat(input.formulaPercent) - (this.formula?.percentTolerance ?? 0))) {
          const name = this.inputTypeList(input).find(i => i.id === input.id).name;
          this.inputsError = `Formula requires ${name} to be ${input.formulaPercent}`;
        }
      });

      return this.laa;
    },
    addInput() {
      const input = {type:'',key:Date.now(),vesselType:'',vesselId:'',quantity:0,laa:0,kg:0,strength:0,percent:0,errorMessage:null,lot:''};   // key is needed to sort properly
      this.blend.inputs.push(input);
      this.inputChangeType(input);
      this.inputsError = null;
      this.selectedInputItem = input.key;
      this.validate(false);
    },
    deleteInput(index) {
      this.blend.inputs.splice(index, 1);
      this.changeLAA(false);
    },
    inputTypeList(input) {
      if (input.type === 'Spirit')
        return this.spirits;
      if (input.type === 'Wine')
        return this.wines;
      if (input.type === 'NonExcise')
        return this.nonExcises;
      return this.items;
    },

    inputChangeType(input, id='', laa=0) {
      input.laa = laa;
      input.quantity = 0;
      input.id = id;

      if (input.type === 'Item') {
        input.quantity = laa;
        input.laa = 0;
        input.lot = '';
        this.inputChangeItem(input);
      }

      this.changeLAA(input.type !== 'Item' && input.type !== '');
    },
    async inputChangeItem(input) {
      const item = this.inputItem(input);
      if (!item.sites && input.id) {
        const results = await this.$apiCall(`query ProductionBlendingEdit($id:ID!, $date:DateTime!) {
                                                  item(id:$id) {
                                                    sites(asof:$date) {siteId,lot,quantity}
                                                  }
                                                }`,
                                            {id:input.id,date:this.blend.date});
        if (!results.error)
          this.$set(item, 'sites', results.data.item.sites);
      }
      if (!this.blendId)
        this.changeItemLot(input)
      this.inputsError = null;
      this.validate(false);
    },
    changeVesselType(input) {
      input.vesselId = '';
      this.inputsError = null;
      this.validate(true);
    },
    inputItem(input) {
      return this.items.find(i => i.id === input.id) || {};
    },
    lotTracking(input) {
      return this.inputItem(input).lotTracking || false;
    },

    async forDateAndSite() {
      this.loading = true;
      for (const input of this.blend.inputs) {
        if (input.type === 'Item') {
          const itemObject = this.inputItem(input);
//          const itemObject = this.items.find(itemObject => itemObject.id === input.id);

          // Get proper quantity and lot info for current date and site
          delete itemObject.sites;
          await this.inputChangeItem(input);

          // Make sure lot is still valid, unless this is an edit where we'll assume it must be valid
          if (!this.blendId && !this.currentLots(input).some(lot => lot === input.lot))
            input.lot = null;
        }
        else if (input.vesselType === 'Tank' && !this.tanks.find(t => t.id === input.vesselId))
          input.vesselId = '';
        else if (input.vesselType === 'BulkContainer' && !this.bulkContainers.find(b => b.id === input.vesselId))
          input.vesselId = '';
      }
      this.loading = false;
      this.inputsError = null;
      this.validate(false);
    },
    async selectFormula(reset=true) {
      this.inputsError = null;
      if (this.adhoc) {
        this.blend.inputs.forEach(input => {delete input.static; delete input.formulaPercent});
        this.validate(true);
        return;
      }
      const formula = this.formulas.find(f => f.id ===this.blend.formulaId);
      this.blend.excise = formula.excise;
      this.blend.approvedFormulation = formula.approvedFormulation;
      this.blend.blendToVolume = formula.blendToVolume;
      this.blend.blendStrength = formula.blendStrength;
      this.laa = this.blend.blendToVolume ? this.blend.producedVolume * this.blend.blendStrength / 100 : this.laa;

      if (!formula.inputs) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionBlendingEdit($id:ID!) {
                                                  formula(id:$id) {
                                                    outputId,inputs{type,id,quantity,strength,minQuantity,maxQuantity,precision}
                                                  }
                                                }`,
                                            {id:formula.id});
        if (!results.error) {
          this.$set(formula, 'outputId', results.data.formula.outputId);
//          results.data.formula.inputs.forEach(input => input.type = input.type === 'NonExcise' ? 'Non-excise' : input.type);
          results.data.formula.inputs.forEach(input => input.kg = 0);
          this.$set(formula, 'inputs', results.data.formula.inputs);
        }
        this.loading = false;
      }

      if (reset) {    // Not done when editing existing blend
        this.blend.outputId = formula.outputId;
        let key = Date.now();
        const oldInputs = this.blend.inputs;
 //       this.blend.inputs.splice(0, this.blend.inputs.length);    // Clear all inputs
        this.blend.inputs = [];
        formula.inputs.forEach(formulaInput => {      // Need to do it this way to be reactive
          // If old input matches required input, take values from that
          const oldInput = oldInputs.find(i => i.type === formulaInput.type && i.id === formulaInput.id);
          const quantity = formula.blendToVolume ? formulaInput.quantity * this.blend.producedVolume : (oldInput?.quantity||0);
          const input = {type:formulaInput.type,key:key++, static:true, strength:0,
                         vesselType:oldInput?.vesselType||'Tank', vesselId:oldInput?.vesselId||'',
                         strength:formulaInput.strength,
                         quantity, kg:formulaInput.kg,
                         laa:oldInput?.laa||0,percent:0,errorMessage:null};
          if (formulaInput.type !== 'Item')
            input.formulaPercent = this.$number(formulaInput.quantity, 1) + '%';
          if (!this.formula.blendToVolume || input.type === 'Item')
            this.inputChangeType(input, formulaInput.id, formulaInput.type === 'Item' ? formulaInput.quantity : input.laa);
          else
            input.id = formulaInput.id;
          this.blend.inputs.push(input);
        });

        // If a scale base is specified, then set initial quantities based on that
        if (formula.scaleBase && formula.scaleToId) {
          const basePercent = formula.inputs.find(i => i.id === formula.scaleToId).quantity;
          formula.inputs.forEach(formulaInput => {
            if (formulaInput.type !== 'Item') {
              const blendInput = this.blend.inputs.find(i => i.id === formulaInput.id);
              blendInput.laa = this.$roundNumber(formulaInput.quantity / basePercent * formula.scaleBase, 3);
            }
          });
        }

        this.changeLAA(true);
      }
      this.validate(true);
    },
    doCalculate() {
      for (const input of this.formula.inputs)
        if (input.type === 'Spirit' || input.type === 'Wine' || input.type === 'NonExcise') {
          const formulaInput = this.blend.inputs.find(i => i.id === input.id);
          formulaInput.laa = input.quantity * this.targetLAA / 100;
        }
      this.changeLAA(true);
      this.calculate = false;
    },
    async refreshLot() {
      if (this.blendId)    // Don't do for editing existing blends
        return;
      this.loading = true;
      const results = await this.$apiCall(`query ProductionBlendingEdit($asof:DateTime!,$prefix:String!) {
                                            lotUnique(prefix:$prefix,date:$asof)
                                          }`,
                                          {asof:this.blend.date,
                                           prefix:{WineReductionNonDutyPaid:'W',SpiritsReductionBulkSpiritsReturnedToSpiritProduction:'S'}[this.blend.excise] ?? 'N'});
      if (!results.error)
        this.blend.lot = results.data.lotUnique;

      this.loading = false;
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      const incomplete = this.blend.inputs.some(item => !item.id ||
                                                (item.type === 'Spirit' || item.type === 'Wine' || item.type === 'NonExcise') && !item.vesselId && this.blend.completed ||
                                                item.type === 'Item' && this.lotTracking(item) && this.required(item.lot)!==true ||
                                                item.quantity < 0 ||
                                                item.strength < 0 || item.strength > 100);
      if (!this.valid || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {inputs,created, ...input} = this.blend;
      if (this.adhoc)
        delete input.formulaId;

      // Deep copy inputs
      input.inputs = [];
      input.inputs = this.blend.inputs.map(input => {
        // eslint-disable-next-line no-unused-vars
        const {key, errorMessage, formulaPercent, percent, ...rest} = input;
        delete rest.static;     // reserved word so have to do it this way
//        rest.type = rest.type === 'Non-excise' ? 'NonExcise' : rest.type;
        rest.vesselType = rest.vesselType === '' ? null : rest.vesselType;
        return rest;
      });
      const results = await this.$apiCall(`mutation ProductionBlendingEdit($input: BlendInput!) {
                                             blendCreate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.blend.id?'Updated':'Created'} blend`});
        if (this.activityId)
          this.$store.dispatch('checkTasks', true);
      }
    }
  }
}
</script>